import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Eye, Edit, Trash2, LogOut, Home as HomeIcon, Users, Building2, AlertCircle, Shield, CreditCard, Calendar, User, Mail, Phone, Building, Pencil, PauseCircle, Lock, PlayCircle, AlertTriangle, MessageSquare, Download, Filter, GraduationCap, ExternalLink, Clock, BarChart3, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { propertyService, authService, subscriptionService } from "../services/api";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to build image URLs with proper encoding
const getImageUrl = (imagePath, { width = 800, quality = 'auto:low' } = {}) => {
  if (!imagePath) return null;
  
  if (imagePath.includes('res.cloudinary.com') && imagePath.includes('/upload/')) {
    const transform = `f_webp,q_${quality},w_${width},c_limit`;
    return imagePath.replace('/upload/', `/upload/${transform}/`);
  }
  
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  
  let normalizedPath = imagePath;
  if (imagePath.startsWith('/uploads/')) {
    normalizedPath = '/api' + imagePath;
  }
  
  const segments = normalizedPath.split('/');
  const encodedSegments = segments.map((segment) => {
    if (segment === '' || segment === 'api' || segment === 'uploads' || segment === 'properties') {
      return segment;
    }
    return encodeURIComponent(segment);
  });
  return `${baseUrl}${encodedSegments.join('/')}`;
};

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [propertyLimitExceeded, setPropertyLimitExceeded] = useState(false);
  const [propertyLimitMessage, setPropertyLimitMessage] = useState("");
  const [togglingProperty, setTogglingProperty] = useState(null);
  const [profileForm, setProfileForm] = useState({
    name: "",
    phone: "",
    company_name: ""
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [activeTab, setActiveTab] = useState("properties");
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsFilter, setLeadsFilter] = useState("all");
  const [learningVideos, setLearningVideos] = useState([]);
  const [crmWebhook, setCrmWebhook] = useState({ available: false, webhook_url: "", webhook_secret: "" });
  const [crmSaving, setCrmSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // First check subscription status (this enforces limits)
      const subCheck = await subscriptionService.checkSubscription();
      if (subCheck.property_limit?.exceeded) {
        setPropertyLimitExceeded(true);
        setPropertyLimitMessage(subCheck.property_limit.message);
      }
      
      const [props, userInfo, subStatus] = await Promise.all([
        propertyService.getMyProperties(),
        authService.getCurrentUser(),
        subscriptionService.getStatus()
      ]);
      setProperties(props);
      setUser(userInfo);
      setSubscription(subStatus);
      
      // Update limit exceeded from status
      if (subStatus.property_limit_exceeded) {
        setPropertyLimitExceeded(true);
        setPropertyLimitMessage(subStatus.property_limit_message);
      }
      
      setProfileForm({
        name: userInfo.name || "",
        phone: userInfo.phone || "",
        company_name: userInfo.company_name || ""
      });
      
      // Load subscription details if user has stripe subscription
      try {
        const details = await subscriptionService.getSubscriptionDetails();
        setSubscriptionDetails(details);
      } catch (e) {
        // User might not have a subscription yet
      }
      
      // Load leads
      loadLeads();
      
      // Load property view stats (Pro+)
      try {
        const statsRes = await axios.get(`${API}/properties/stats/summary`);
        if (statsRes.data?.stats) setPropertyStats(statsRes.data.stats);
      } catch (e) { /* Pro+ only */ }
    } catch (error) {
      if (error.response?.status === 401) {
        authService.logout();
        navigate("/login");
      } else {
        toast.error("Error al cargar datos");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadLeads = async () => {
    setLeadsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data);
    } catch (error) {
      console.error("Error loading leads:", error);
    } finally {
      setLeadsLoading(false);
    }
  };

  const loadLearningVideos = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/learning-videos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLearningVideos(response.data || []);
    } catch (error) {
      console.error("Error loading learning videos:", error);
    }
  };

  const getEmbedUrl = (url) => {
    if (!url) return null;
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
    if (loomMatch) return `https://www.loom.com/embed/${loomMatch[1]}`;
    return url;
  };

  const loadCrmWebhook = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/user/crm-webhook`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCrmWebhook(res.data);
    } catch (error) {
      console.error("Error loading CRM webhook:", error);
    }
  };

  const handleSaveCrmWebhook = async () => {
    setCrmSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/user/crm-webhook`, {
        webhook_url: crmWebhook.webhook_url,
        webhook_secret: crmWebhook.webhook_secret
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Webhook guardado correctamente");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setCrmSaving(false);
    }
  };

  const handleTestCrmWebhook = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/user/crm-webhook/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        toast.success(`Test enviado (status: ${res.data.status_code})`);
      } else {
        toast.error(`Error: ${res.data.error}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al probar webhook");
    }
  };

  const exportLeadsCSV = () => {
    const filteredLeads = leadsFilter === "all" 
      ? leads 
      : leads.filter(l => l.property_id === leadsFilter);
    
    const headers = ["Nombre", "Email", "Teléfono", "Mensaje", "Fuente", "Propiedad", "Fecha"];
    const rows = filteredLeads.map(l => [
      l.name,
      l.email,
      l.phone,
      (l.message || "").replace(/,/g, ";"),
      l.source === "whatsapp" ? "WhatsApp" : "Formulario",
      l.property_title || "",
      new Date(l.created_at).toLocaleDateString("es-MX")
    ]);
    
    const csv = [headers.join(","), ...rows.map(r => r.map(v => `"${v}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "leads_inmopages.csv";
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Leads exportados");
  };


  const handleDelete = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar esta propiedad?")) {
      return;
    }

    try {
      await propertyService.delete(id);
      toast.success("Propiedad eliminada");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar propiedad");
    }
  };

  const handleTogglePropertyStatus = async (propertyId, currentStatus) => {
    const action = currentStatus === "active" ? "pause" : "activate";
    setTogglingProperty(propertyId);
    
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${API}/api/properties/${propertyId}/toggle-status?action=${action}`);
      toast.success(action === "activate" ? "Propiedad activada" : "Propiedad pausada");
      setPropertyLimitExceeded(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cambiar estado de propiedad");
    } finally {
      setTogglingProperty(null);
    }
  };

  const handleLogout = () => {
    authService.logout();
    navigate("/login");
  };

  const handleOpenBillingPortal = async () => {
    // If user has no plan selected (pending_payment), send to pricing page first
    if (user && (user.plan === "trial" || user.subscription_status === "pending_payment")) {
      navigate("/precios");
      return;
    }
    
    // If user has a plan selected, go directly to checkout/billing
    setBillingLoading(true);
    try {
      await subscriptionService.openBillingPortal();
    } catch (error) {
      toast.error("No se pudo abrir el portal de facturación");
    } finally {
      setBillingLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await axios.put(`${API}/api/auth/profile`, profileForm);
      toast.success("Perfil actualizado");
      setEditingProfile(false);
      loadData();
    } catch (error) {
      toast.error("Error al guardar perfil");
    } finally {
      setSavingProfile(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("es-MX", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const isPendingPayment = subscription?.status === "pending_payment";
  const daysSinceRegister = user?.created_at ? Math.floor((Date.now() - new Date(user.created_at).getTime()) / (1000 * 60 * 60 * 24)) : 0;
  const isProPlus = ["pro", "premium", "agencia"].includes(user?.plan);
  
  // Property view stats (Pro+)
  const [propertyStats, setPropertyStats] = useState({});
  const [selectedPropertyStats, setSelectedPropertyStats] = useState(null);
  
  // Pending payment users can create 1 draft property to try the platform
  const canCreateProperty = isPendingPayment 
    ? properties.length < 1 
    : (subscription?.is_active && subscription?.active_properties < subscription?.max_properties);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_proplandpro/artifacts/d99lgtpt_inmopages-fondoblanco.png" 
                alt="InmoPages" 
                className="h-8"
              />
            </Link>
            <div className="flex items-center space-x-4">
              {user && (
                <span className="text-sm text-gray-600 hidden sm:block">
                  {user.name}
                </span>
              )}
              {user?.role === "admin" && (
                <Link to="/super-admin">
                  <Button variant="outline" size="sm" className="border-brand-purple text-brand-purple hover:bg-purple-50">
                    <Shield className="w-4 h-4 mr-2" />
                    Super Admin
                  </Button>
                </Link>
              )}
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Salir
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-hidden">
        {/* Subscription Banner - only for users who had an active sub that expired (NOT pending_payment) */}
        {subscription && !subscription.is_active && !isPendingPayment && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
              <div>
                <p className="font-semibold text-red-800">Tu suscripción ha expirado</p>
                <p className="text-sm text-red-600">Tus propiedades están pausadas. Renueva para reactivarlas.</p>
              </div>
            </div>
            <Link to="/precios">
              <Button className="bg-red-600 hover:bg-red-700 text-white">
                Ver Planes
              </Button>
            </Link>
          </div>
        )}

        {/* Trial Mode Banner - pending_payment users */}
        {isPendingPayment && (
          <div className="mb-6 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-5" data-testid="trial-mode-banner">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="bg-brand-purple/10 p-2 rounded-lg flex-shrink-0">
                  <Clock className="w-5 h-5 text-brand-purple" />
                </div>
                <div>
                  <p className="font-bold text-gray-900">Modo Prueba</p>
                  <p className="text-sm text-gray-600 mt-0.5">
                    Explora el dashboard y crea tu primera propiedad. 
                    {properties.length > 0 
                      ? " Tu propiedad no será visible al público hasta que actives tu plan."
                      : " Crea una propiedad de prueba para ver cómo funciona."
                    }
                  </p>
                  {daysSinceRegister > 0 && (
                    <p className="text-xs text-purple-500 mt-1 font-medium">
                      Llevas {daysSinceRegister} {daysSinceRegister === 1 ? 'día' : 'días'} sin publicar tu propiedad
                    </p>
                  )}
                </div>
              </div>
              <Link to="/precios" className="flex-shrink-0">
                <Button className="bg-brand-purple hover:bg-brand-pink text-white w-full sm:w-auto">
                  Activar mi Plan
                </Button>
              </Link>
            </div>
          </div>
        )}

        {subscription && subscription.status === "trialing" && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-blue-600 mr-3" />
              <div>
                <p className="font-semibold text-blue-800">Prueba Gratuita</p>
                <p className="text-sm text-blue-600">
                  Tu prueba termina el {subscription.trial_ends_at ? formatDate(subscription.trial_ends_at) : 'pronto'}
                </p>
              </div>
            </div>
            <Link to="/precios">
              <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50">
                Elegir Plan
              </Button>
            </Link>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Mis Propiedades</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {subscription?.active_properties || 0}
                    <span className="text-lg text-gray-400">/{subscription?.max_properties || 0}</span>
                  </p>
                </div>
                <Building2 className="w-10 h-10 text-brand-purple opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="hover:shadow-md transition-shadow cursor-pointer group" onClick={() => navigate('/precios')}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Plan Actual</p>
                  <p className="text-2xl font-bold text-gray-900 capitalize">
                    {subscription?.plan_name || 'Sin plan'}
                  </p>
                  <p className="text-xs text-brand-purple group-hover:text-brand-pink mt-1 font-medium">
                    Cambiar plan →
                  </p>
                </div>
                <Users className="w-10 h-10 text-brand-purple opacity-50 group-hover:opacity-100 transition-opacity" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Estado</p>
                  <p className={`text-xl font-bold capitalize ${
                    subscription?.is_active ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {subscription?.status === 'trialing' ? 'Prueba Gratis' :
                     subscription?.status === 'active' ? 'Activo' :
                     subscription?.status === 'expired' ? 'Expirado' :
                     subscription?.status === 'pending_payment' ? 'Pago Pendiente' : 'Inactivo'}
                  </p>
                </div>
                <div className={`w-4 h-4 rounded-full ${
                  subscription?.is_active ? 'bg-green-500' : 'bg-red-500'
                }`} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Profile Section */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center text-lg">
                <User className="w-5 h-5 mr-2 text-brand-purple" />
                Mi Perfil
              </CardTitle>
              {!editingProfile && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setEditingProfile(true)}
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Editar
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {editingProfile ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="profile-name">Nombre completo</Label>
                    <Input
                      id="profile-name"
                      value={profileForm.name}
                      onChange={(e) => setProfileForm({...profileForm, name: e.target.value})}
                      placeholder="Tu nombre"
                    />
                  </div>
                  <div>
                    <Label htmlFor="profile-phone">WhatsApp</Label>
                    <Input
                      id="profile-phone"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                      placeholder="+52 333 123 4567"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="profile-company">Empresa / Inmobiliaria</Label>
                    <Input
                      id="profile-company"
                      value={profileForm.company_name}
                      onChange={(e) => setProfileForm({...profileForm, company_name: e.target.value})}
                      placeholder="Nombre de tu empresa"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                    className="bg-brand-purple hover:bg-brand-pink"
                  >
                    {savingProfile ? "Guardando..." : "Guardar Cambios"}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setEditingProfile(false);
                      setProfileForm({
                        name: user?.name || "",
                        phone: user?.phone || "",
                        company_name: user?.company_name || ""
                      });
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Correo electrónico</p>
                    <p className="font-medium text-gray-900">{user?.email}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Nombre</p>
                    <p className="font-medium text-gray-900">{user?.name || "-"}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">WhatsApp</p>
                    <p className="font-medium text-gray-900">{user?.phone || "-"}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 md:col-span-2">
                  <Building className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Empresa / Inmobiliaria</p>
                    <p className="font-medium text-gray-900">{user?.company_name || "-"}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Billing Section - Shown for all users */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center text-lg">
              <CreditCard className="w-5 h-5 mr-2 text-brand-purple" />
              Facturación y Suscripción
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {/* Current Plan */}
              <div>
                <p className="text-sm text-gray-500 mb-1">Plan actual</p>
                <p className="font-medium text-gray-900 capitalize">
                  {subscription?.plan || user?.plan || 'Sin plan'}
                </p>
              </div>
              
              {/* Payment Method */}
              <div>
                <p className="text-sm text-gray-500 mb-1">Método de pago</p>
                {subscriptionDetails?.card_last4 ? (
                  <p className="font-medium text-gray-900">
                    {subscriptionDetails.card_brand?.toUpperCase()} •••• {subscriptionDetails.card_last4}
                  </p>
                ) : (
                  <p className="text-gray-400">No registrado</p>
                )}
              </div>
              
              {/* Next Billing */}
              <div>
                <p className="text-sm text-gray-500 mb-1">Próximo cobro</p>
                {subscriptionDetails?.next_billing_date ? (
                  <p className="font-medium text-green-600">
                    {formatDate(subscriptionDetails.next_billing_date)}
                  </p>
                ) : subscription?.next_payment_date ? (
                  <p className="font-medium text-green-600">
                    {formatDate(subscription.next_payment_date)}
                  </p>
                ) : subscription?.status === 'trialing' && subscription?.trial_ends_at ? (
                  <div>
                    <p className="font-medium text-yellow-600">
                      {formatDate(subscription.trial_ends_at)}
                    </p>
                    <span className="text-xs text-gray-400">Fin de prueba</span>
                  </div>
                ) : user?.role === 'admin' ? (
                  <p className="text-gray-400 text-sm">N/A (Admin)</p>
                ) : (
                  <p className="text-gray-400">-</p>
                )}
              </div>
              
              {/* Amount */}
              <div>
                <p className="text-sm text-gray-500 mb-1">Monto mensual</p>
                {subscriptionDetails?.amount ? (
                  <p className="font-medium text-gray-900">
                    ${(subscriptionDetails.amount / 100).toLocaleString()} {subscriptionDetails.currency?.toUpperCase()}
                  </p>
                ) : (
                  <p className="text-gray-400">-</p>
                )}
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t flex flex-wrap gap-3">
              {subscriptionDetails?.stripe_subscription_id ? (
                <Button 
                  variant="outline" 
                  onClick={handleOpenBillingPortal}
                  disabled={billingLoading}
                  className="border-brand-purple text-brand-purple hover:bg-purple-50"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {billingLoading ? "Abriendo..." : "Gestionar Suscripción"}
                </Button>
              ) : (
                <Link to="/precios">
                  <Button className="bg-brand-purple hover:bg-brand-pink text-white">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Agregar método de pago
                  </Button>
                </Link>
              )}
              
              <Link to="/precios">
                <Button variant="outline">
                  Cambiar Plan
                </Button>
              </Link>
            </div>
            
            {subscriptionDetails?.stripe_subscription_id && (
              <p className="text-xs text-gray-500 mt-2">
                Actualiza tu tarjeta, descarga facturas o cancela tu suscripción
              </p>
            )}
          </CardContent>
        </Card>

        {/* Tab Navigation */}
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 mb-6" data-testid="dashboard-tabs">
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit min-w-0">
            <button
              onClick={() => setActiveTab("properties")}
              className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === "properties" 
                  ? "bg-white text-brand-purple shadow-sm" 
                  : "text-gray-600 hover:text-gray-900"
              }`}
              data-testid="tab-properties"
            >
              <Building2 className="w-4 h-4 inline mr-1 sm:mr-2" />
              Propiedades ({properties.length})
            </button>
            <button
              onClick={() => setActiveTab("leads")}
              className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === "leads" 
                  ? "bg-white text-brand-purple shadow-sm" 
                  : "text-gray-600 hover:text-gray-900"
              }`}
              data-testid="tab-leads"
            >
              <MessageSquare className="w-4 h-4 inline mr-1 sm:mr-2" />
              Leads ({leads.length})
            </button>
            <button
              onClick={() => { setActiveTab("learning"); loadLearningVideos(); }}
              className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === "learning" 
                  ? "bg-white text-brand-purple shadow-sm" 
                  : "text-gray-600 hover:text-gray-900"
              }`}
              data-testid="tab-learning"
            >
              <GraduationCap className="w-4 h-4 inline mr-1 sm:mr-2" />
              Aprendizaje
            </button>
            <button
              onClick={() => { setActiveTab("integrations"); loadCrmWebhook(); }}
              className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === "integrations" 
                  ? "bg-white text-brand-purple shadow-sm" 
                  : "text-gray-600 hover:text-gray-900"
              }`}
              data-testid="tab-integrations"
            >
              <ExternalLink className="w-4 h-4 inline mr-1 sm:mr-2" />
              Integraciones
            </button>
          </div>
        </div>

        {activeTab === "properties" && (
        <>
        {/* Actions */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Mis Propiedades</h2>
          <div className="flex items-center gap-2">
            {canCreateProperty ? (
              <Link to="/admin/properties/new">
                <Button className="bg-brand-purple hover:bg-brand-pink text-white" size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Propiedad
                </Button>
              </Link>
            ) : (
              <div>
                <p className="text-sm text-gray-500 mb-1">Límite alcanzado</p>
                <Link to="/precios">
                  <Button variant="outline" size="sm">
                    Actualizar Plan
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Property Limit Warning */}
        {propertyLimitExceeded && (
          <Card className="mb-6 border-amber-300 bg-amber-50">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-amber-800 mb-1">
                    Límite de propiedades excedido
                  </h4>
                  <p className="text-sm text-amber-700 mb-3">
                    {propertyLimitMessage || `Tu plan permite ${subscription?.max_properties || 1} propiedad(es) activa(s). Selecciona cuáles deseas mantener activas.`}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Link to="/precios">
                      <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white">
                        Actualizar mi plan
                      </Button>
                    </Link>
                    <span className="text-xs text-amber-600 self-center">
                      o selecciona qué propiedades activar abajo
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Properties List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Cargando...</p>
          </div>
        ) : properties.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                No tienes propiedades
              </h3>
              <p className="text-gray-500 mb-6">
                Crea tu primera propiedad y comienza a generar leads
              </p>
              {canCreateProperty && (
                <Link to="/admin/properties/new">
                  <Button className="bg-brand-purple hover:bg-brand-pink text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Crear Propiedad
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property) => {
              // Check if subscription is expired/inactive
              const isSubscriptionInactive = subscription && 
                (subscription.status === 'expired' || 
                 subscription.status === 'canceled' || 
                 subscription.status === 'past_due');
              
              // Check if property is paused due to limit
              const isPausedDueToLimit = property.status === 'paused_limit' || property.status === 'paused_subscription';
              const isDraft = property.status === 'draft' || !property.is_published;
              
              // Determine display status
              const displayStatus = isDraft ? 'draft' :
                                    isSubscriptionInactive ? 'paused' : 
                                    isPausedDueToLimit ? 'paused_limit' : property.status;
              
              return (
              <Card key={property.id} className={`overflow-hidden transition-shadow ${isDraft ? 'border-amber-300 border-dashed border-2' : isSubscriptionInactive || isPausedDueToLimit ? 'opacity-90 border-amber-200' : 'hover:shadow-lg'}`}>
                <div className="relative">
                  {property.main_image ? (
                    <img
                      src={getImageUrl(property.main_image)}
                      alt={property.title}
                      className={`w-full h-48 object-cover ${isSubscriptionInactive || isPausedDueToLimit ? 'grayscale-[50%]' : ''}`}
                    />
                  ) : property.images && property.images.length > 0 ? (
                    <img
                      src={getImageUrl(property.images[0])}
                      alt={property.title}
                      className={`w-full h-48 object-cover ${isSubscriptionInactive || isPausedDueToLimit ? 'grayscale-[50%]' : ''}`}
                    />
                  ) : (
                    <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                      <Building2 className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                  {isDraft && (
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                      <div className="bg-amber-500 text-white px-4 py-2 rounded-lg font-semibold shadow-lg text-sm">
                        Borrador — No visible al público
                      </div>
                    </div>
                  )}
                  {(isSubscriptionInactive || isPausedDueToLimit) && (
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <div className="bg-white/95 px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg">
                        <PauseCircle className="w-5 h-5 text-amber-600" />
                        <span className="font-semibold text-amber-700">
                          {isPausedDueToLimit ? 'Pausada (Límite)' : 'Pausada'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-lg line-clamp-1 flex-1 mr-2">{property.title}</h3>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {/* View count badge (Pro+) */}
                      {isProPlus ? (
                        <button
                          onClick={() => {
                            axios.get(`${API}/properties/${property.id}/stats`).then(r => setSelectedPropertyStats({ ...r.data, title: property.title })).catch(() => {});
                          }}
                          className="flex items-center gap-1 text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full hover:bg-blue-100 transition-colors"
                          data-testid={`property-views-${property.id}`}
                          title="Ver estadísticas"
                        >
                          <BarChart3 className="w-3 h-3" />
                          {propertyStats[property.id]?.views ?? 0}
                        </button>
                      ) : (
                        <button
                          onClick={() => setSelectedPropertyStats({ locked: true, title: property.title })}
                          className="flex items-center gap-1 text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full hover:bg-amber-50 hover:text-amber-700 transition-colors"
                          data-testid={`property-views-locked-${property.id}`}
                          title="Disponible desde el plan Pro"
                        >
                          <Lock className="w-3 h-3" />
                          <BarChart3 className="w-3 h-3" />
                        </button>
                      )}
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      displayStatus === 'draft' ? 'bg-amber-100 text-amber-700' :
                      displayStatus === 'paused' || displayStatus === 'paused_limit' ? 'bg-amber-100 text-amber-700' :
                      displayStatus === 'active' ? 'bg-green-100 text-green-700' :
                      displayStatus === 'sold' ? 'bg-red-100 text-red-700' :
                      displayStatus === 'rented' ? 'bg-orange-100 text-orange-700' :
                      displayStatus === 'paused_subscription' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {displayStatus === 'draft' ? 'Borrador' :
                       displayStatus === 'paused' || displayStatus === 'paused_limit' ? 'Pausada' :
                       displayStatus === 'paused_subscription' ? 'Suscripción Vencida' :
                       displayStatus === 'active' ? 'Activa' :
                       displayStatus === 'sold' ? 'Vendida' :
                       displayStatus === 'rented' ? 'Rentada' : 'Reservada'}
                    </span>
                    </div>
                  </div>
                  <p className="text-brand-purple font-bold text-xl mb-2">
                    {formatPrice(property.price)}
                  </p>
                  <p className="text-sm text-gray-500 mb-4 line-clamp-1">
                    {property.location}
                  </p>
                  
                  {isSubscriptionInactive ? (
                    <div className="space-y-2">
                      <p className="text-xs text-amber-600 flex items-center gap-1">
                        <Lock className="w-3 h-3" />
                        Reactiva tu suscripción para editar
                      </p>
                      <Button 
                        onClick={handleOpenBillingPortal}
                        disabled={billingLoading}
                        className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                        size="sm"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        {user?.plan === "trial" || subscription?.is_pending_payment ? "Elegir Plan" : "Reactivar Suscripción"}
                      </Button>
                    </div>
                  ) : isPausedDueToLimit && subscription?.is_active ? (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => handleTogglePropertyStatus(property.id, property.status)}
                          disabled={togglingProperty === property.id}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                          size="sm"
                        >
                          <PlayCircle className="w-4 h-4 mr-2" />
                          {togglingProperty === property.id ? "Activando..." : "Activar"}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(property.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Eliminar propiedad"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 text-center">
                        Activa o elimina propiedades para ajustar a tu plan
                      </p>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Link 
                        to={`/propiedades/${property.slug}`} 
                        target="_blank"
                        className="flex-1"
                        title={isDraft ? "Vista previa (solo tú puedes verla)" : "Ver propiedad pública"}
                      >
                        <Button variant="outline" size="sm" className={`w-full ${isDraft ? 'border-amber-300 text-amber-700 hover:bg-amber-50' : ''}`}>
                          <Eye className="w-4 h-4 mr-1" />
                          {isDraft ? 'Vista previa' : 'Ver'}
                        </Button>
                      </Link>
                      <Link to={`/admin/properties/edit/${property.id}`} className="flex-1">
                        <Button variant="outline" size="sm" className="w-full">
                          <Edit className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                      </Link>
                      {property.status === 'active' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTogglePropertyStatus(property.id, property.status)}
                          disabled={togglingProperty === property.id}
                          className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          title="Pausar propiedad"
                        >
                          <PauseCircle className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(property.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
              );
            })}
          </div>
        )}
        </>
        )}

        {/* Leads Tab */}
        {activeTab === "leads" && (
          <div data-testid="leads-section">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Mis Leads</h2>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={leadsFilter}
                  onChange={(e) => setLeadsFilter(e.target.value)}
                  className="border rounded-lg px-3 py-2 text-sm flex-1 min-w-[150px]"
                  data-testid="leads-filter"
                >
                  <option value="all">Todas las propiedades</option>
                  {properties.map(p => (
                    <option key={p.id} value={p.id}>{p.title}</option>
                  ))}
                </select>
                <Button 
                  onClick={exportLeadsCSV}
                  variant="outline" 
                  size="sm"
                  disabled={leads.length === 0}
                  data-testid="export-leads-btn"
                  className="whitespace-nowrap"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exportar CSV
                </Button>
              </div>
            </div>

            {leadsLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-brand-purple mx-auto"></div>
                <p className="mt-2 text-gray-500">Cargando leads...</p>
              </div>
            ) : leads.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Sin leads todavía</h3>
                  <p className="text-gray-500">Cuando alguien envíe un formulario de contacto o te escriba por WhatsApp desde tus landing pages, aparecerá aquí.</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="leads-table">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left p-4 text-sm font-medium text-gray-600">Nombre</th>
                          <th className="text-left p-4 text-sm font-medium text-gray-600">Contacto</th>
                          <th className="text-left p-4 text-sm font-medium text-gray-600">Propiedad</th>
                          <th className="text-left p-4 text-sm font-medium text-gray-600">Fuente</th>
                          <th className="text-left p-4 text-sm font-medium text-gray-600">Mensaje</th>
                          <th className="text-left p-4 text-sm font-medium text-gray-600">Fecha</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(leadsFilter === "all" ? leads : leads.filter(l => l.property_id === leadsFilter)).map((lead) => (
                          <tr key={lead.id} className={`border-b hover:bg-gray-50 ${!lead.is_read ? 'bg-blue-50/50' : ''}`}>
                            <td className="p-4">
                              <p className="font-medium text-gray-900">{lead.name}</p>
                            </td>
                            <td className="p-4">
                              <div className="space-y-1">
                                <a href={`mailto:${lead.email}`} className="text-sm text-brand-purple hover:underline block">{lead.email}</a>
                                <a href={`tel:${lead.phone}`} className="text-sm text-gray-600 hover:underline block">{lead.phone}</a>
                              </div>
                            </td>
                            <td className="p-4">
                              <Link 
                                to={`/propiedades/${lead.property_slug}`} 
                                target="_blank"
                                className="text-sm text-brand-purple hover:underline"
                              >
                                {lead.property_title}
                              </Link>
                            </td>
                            <td className="p-4">
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                lead.source === 'whatsapp' 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-purple-100 text-purple-700'
                              }`}>
                                {lead.source === 'whatsapp' ? 'WhatsApp' : 'Formulario'}
                              </span>
                            </td>
                            <td className="p-4">
                              <p className="text-sm text-gray-600 max-w-xs truncate">{lead.message || '-'}</p>
                            </td>
                            <td className="p-4">
                              <p className="text-sm text-gray-500">{new Date(lead.created_at).toLocaleDateString("es-MX")}</p>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Learning Videos Tab */}
        {activeTab === "learning" && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Centro de Aprendizaje</h2>
            {learningVideos.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <GraduationCap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">Proximamente</h3>
                  <p className="text-gray-400">Estamos preparando contenido exclusivo para ayudarte a sacar el maximo provecho de InmoPages.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {learningVideos.map((video) => (
                  <Card key={video.id} className="overflow-hidden" data-testid={`learning-video-${video.id}`}>
                    <div className="aspect-video bg-gray-900">
                      <iframe
                        src={getEmbedUrl(video.video_url)}
                        title={video.title}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900">{video.title}</h3>
                          {video.description && <p className="text-sm text-gray-500 mt-1">{video.description}</p>}
                        </div>
                        <span className="px-2 py-0.5 bg-purple-100 text-brand-purple rounded text-xs capitalize shrink-0 ml-2">{video.category}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Integrations Tab */}
        {activeTab === "integrations" && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Integraciones CRM</h2>
            
            {!crmWebhook.available ? (
              <Card>
                <CardContent className="text-center py-12">
                  <Lock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">Disponible desde Plan Pro</h3>
                  <p className="text-gray-400 mb-4">Conecta tu CRM para recibir leads automaticamente en GHL, N8N, Zapier, Make y mas.</p>
                  <a href="/precios" className="text-brand-purple hover:text-brand-pink font-medium">Ver planes</a>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Webhook de Leads</CardTitle>
                  <p className="text-sm text-gray-500">
                    Cada vez que recibas un lead, enviaremos los datos a esta URL automaticamente. 
                    Conecta con GHL, N8N, Zapier, Make o cualquier CRM que soporte webhooks.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">URL del Webhook</label>
                    <input
                      type="url"
                      placeholder="https://hooks.zapier.com/hooks/catch/..."
                      value={crmWebhook.webhook_url || ""}
                      onChange={(e) => setCrmWebhook({...crmWebhook, webhook_url: e.target.value})}
                      className="w-full border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand-purple focus:border-transparent"
                      data-testid="crm-webhook-url"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Secret (opcional)</label>
                    <input
                      type="text"
                      placeholder="Secret para verificar autenticidad"
                      value={crmWebhook.webhook_secret || ""}
                      onChange={(e) => setCrmWebhook({...crmWebhook, webhook_secret: e.target.value})}
                      className="w-full border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand-purple focus:border-transparent"
                      data-testid="crm-webhook-secret"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handleSaveCrmWebhook}
                      disabled={crmSaving}
                      className="bg-brand-purple hover:bg-brand-pink text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                      data-testid="save-crm-webhook-btn"
                    >
                      {crmSaving ? "Guardando..." : "Guardar"}
                    </button>
                    {crmWebhook.webhook_url && (
                      <button
                        onClick={handleTestCrmWebhook}
                        className="border border-brand-purple text-brand-purple hover:bg-purple-50 px-6 py-2.5 rounded-lg text-sm font-medium transition-colors"
                        data-testid="test-crm-webhook-btn"
                      >
                        Enviar Test
                      </button>
                    )}
                  </div>
                  
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-700 text-sm mb-2">Ejemplo de payload que recibiras:</h4>
                    <pre className="text-xs text-gray-600 overflow-x-auto bg-white p-3 rounded border">{JSON.stringify({
                      event: "new_lead",
                      source: "inmopages",
                      lead: { name: "Juan Perez", email: "juan@email.com", phone: "3311234567", message: "Me interesa" },
                      property: { title: "Casa en Zapopan", slug: "casa-en-zapopan", type: "casa", price: 3500000 }
                    }, null, 2)}</pre>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Property Stats Modal */}
        {selectedPropertyStats && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedPropertyStats(null)}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()} data-testid="property-stats-modal">
              <div className="p-5 border-b flex items-center justify-between sticky top-0 bg-white rounded-t-xl">
                <div>
                  <h3 className="font-bold text-gray-900">Estadísticas</h3>
                  <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">{selectedPropertyStats.title}</p>
                </div>
                <button onClick={() => setSelectedPropertyStats(null)} className="p-1 hover:bg-gray-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              {selectedPropertyStats.locked ? (
                <div className="p-8 text-center" data-testid="stats-upgrade-prompt">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-brand-purple to-brand-pink flex items-center justify-center">
                    <Lock className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Estadísticas Pro</h4>
                  <p className="text-gray-600 mb-6">
                    Las estadísticas de visitas están disponibles desde el <strong>plan Pro</strong>.
                    Conoce cuántas personas ven tus propiedades, de dónde vienen y mucho más.
                  </p>
                  <ul className="text-left text-sm text-gray-700 space-y-2 mb-6 max-w-sm mx-auto">
                    <li className="flex items-start gap-2">
                      <BarChart3 className="w-4 h-4 text-brand-purple flex-shrink-0 mt-0.5" />
                      <span>Visitas totales y visitantes únicos</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <BarChart3 className="w-4 h-4 text-brand-purple flex-shrink-0 mt-0.5" />
                      <span>Gráfico de visitas por día (últimos 30 días)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <BarChart3 className="w-4 h-4 text-brand-purple flex-shrink-0 mt-0.5" />
                      <span>Fuentes de tráfico (de dónde vienen tus visitas)</span>
                    </li>
                  </ul>
                  <button
                    onClick={() => { setSelectedPropertyStats(null); navigate("/precios"); }}
                    className="w-full bg-gradient-to-r from-brand-purple to-brand-pink text-white font-semibold py-3 rounded-lg hover:opacity-90 transition-opacity"
                    data-testid="upgrade-plan-btn"
                  >
                    Mejorar mi plan
                  </button>
                </div>
              ) : (
              <div className="p-5 space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-blue-600">{selectedPropertyStats.total_views}</p>
                    <p className="text-sm text-blue-600/70 mt-1">Visitas totales</p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-purple-600">{selectedPropertyStats.unique_visitors}</p>
                    <p className="text-sm text-purple-600/70 mt-1">Visitantes únicos</p>
                  </div>
                </div>
                
                {/* Daily Chart */}
                {selectedPropertyStats.daily_views && selectedPropertyStats.daily_views.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-700 text-sm mb-3">Visitas por día (últimos {selectedPropertyStats.period_days} días)</h4>
                    <div className="space-y-1.5">
                      {selectedPropertyStats.daily_views.slice(-14).map((day) => {
                        const maxViews = Math.max(...selectedPropertyStats.daily_views.map(d => d.views), 1);
                        const barWidth = (day.views / maxViews) * 100;
                        return (
                          <div key={day.date} className="flex items-center gap-2 text-xs">
                            <span className="w-16 text-gray-400 flex-shrink-0">
                              {new Date(day.date + 'T00:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}
                            </span>
                            <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                              <div className="bg-blue-500 h-full rounded-full transition-all flex items-center justify-end pr-1.5" style={{ width: `${Math.max(barWidth, 8)}%` }}>
                                <span className="text-white text-xs font-medium">{day.views}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Top Referrers */}
                {selectedPropertyStats.top_referrers && selectedPropertyStats.top_referrers.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-700 text-sm mb-3">Fuentes de tráfico</h4>
                    <div className="space-y-2">
                      {selectedPropertyStats.top_referrers.map((ref, i) => {
                        let sourceName = ref.source;
                        try {
                          sourceName = new URL(ref.source).hostname;
                        } catch(e) {}
                        return (
                          <div key={i} className="flex items-center justify-between text-sm bg-gray-50 rounded-lg px-3 py-2">
                            <span className="text-gray-700 truncate max-w-[250px]">{sourceName}</span>
                            <span className="font-semibold text-gray-900 ml-2">{ref.views}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {selectedPropertyStats.total_views === 0 && (
                  <div className="text-center py-6 text-gray-400">
                    <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    <p>Aún no hay visitas registradas</p>
                    <p className="text-sm mt-1">Comparte tu landing para empezar a recibir visitas</p>
                  </div>
                )}
              </div>
              )}
            </div>
          </div>
        )}

        {/* Support Button */}
        <div className="fixed bottom-6 right-6 z-40">
          {user?.plan === "premium" || user?.plan === "agencia" ? (
            <a
              href="https://wa.me/523332509484?text=Hola,%20necesito%20soporte%20de%20InmoPages"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-5 py-3 rounded-full shadow-lg transition-all hover:scale-105"
              data-testid="support-whatsapp-btn"
            >
              <MessageSquare className="w-5 h-5" />
              <span className="font-medium text-sm">Soporte WhatsApp</span>
            </a>
          ) : (
            <a
              href="mailto:soporte@inmopages.app?subject=Soporte%20InmoPages"
              className="flex items-center gap-2 bg-brand-purple hover:bg-brand-pink text-white px-5 py-3 rounded-full shadow-lg transition-all hover:scale-105"
              data-testid="support-email-btn"
            >
              <Mail className="w-5 h-5" />
              <span className="font-medium text-sm">Soporte</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
